import type { ReactNode } from 'react';
import clsx from 'clsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';

export interface AnalyticsColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  className?: string;
  headerClassName?: string;
}

interface AnalyticsDataTableProps<T> {
  columns: AnalyticsColumn<T>[];
  data: T[];
  rowKey: (row: T, index: number) => string;
  emptyMessage?: string;
  truncationBanner?: ReactNode;
  className?: string;
  onRowClick?: (row: T) => void;
  selectedRowKey?: string | null;
  getRowClassName?: (row: T) => string | undefined;
  fillHeight?: boolean;
}

export function AnalyticsDataTable<T>({
  columns,
  data,
  rowKey,
  emptyMessage = 'No data available.',
  truncationBanner,
  className,
  onRowClick,
  selectedRowKey,
  getRowClassName,
  fillHeight = false,
}: AnalyticsDataTableProps<T>) {
  return (
    <div
      className={clsx(
        'overflow-hidden rounded-xl border border-[#E2E5EA] bg-white',
        fillHeight && 'flex min-h-0 flex-1 flex-col',
        className,
      )}
    >
      <div className={clsx('overflow-auto', fillHeight ? 'min-h-0 flex-1' : 'max-h-[480px]')}>
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-page-bg">
            <TableRow>
              {columns.map((col) => (
                <TableHead
                  key={col.id}
                  className={clsx(
                    'whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-blueprint-blue',
                    col.headerClassName,
                  )}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-sm text-heritage-grey"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => {
                const key = rowKey(row, idx);
                return (
                <TableRow
                  key={key}
                  className={clsx(
                    'border-t border-[#E2E5EA] hover:bg-[rgba(0,39,63,0.02)]',
                    onRowClick && 'cursor-pointer',
                    selectedRowKey === key && 'bg-[rgba(0,39,63,0.06)]',
                    getRowClassName?.(row),
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} className={clsx('text-sm text-text-grey', col.className)}>
                      {col.cell(row, idx)}
                    </TableCell>
                  ))}
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {truncationBanner}
    </div>
  );
}
